import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton } from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import BlockIcon from '@mui/icons-material/Block';

export interface Representative {
  name: string;
  mobile?: string;
  id?: number;
}

interface RepresentativeCardProps {
  representatives?: Representative[];
  children: React.ReactNode; // کارت اصلی
  /** عرض کارت (پیش‌فرض: 290). رشته هم می‌پذیرد، مثل '100%' */
  cardWidth?: number | string;
  cardHeight?: number; // ارتفاع کارت اصلی (پیش‌فرض: 150)
  representativeHeight?: number; // ارتفاع هر کارت نماینده (پیش‌فرض: 35)
  expandedHeight?: number; // ارتفاع کارت نماینده وقتی باز است (پیش‌فرض: 80)
  onRemoveRepresentative?: (rep: Representative) => void;
  onRevokeAccess?: (rep: Representative) => void;
}

const RepresentativeCard: React.FC<RepresentativeCardProps> = ({
  representatives = [],
  children,
  cardWidth = 290,
  cardHeight = 150,
  representativeHeight = 35,
  expandedHeight = 80,
  onRemoveRepresentative,
  onRevokeAccess,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Space reserved *below* the main card for the stacked representative cards.
  // The main card sizes itself, so only this tail is measured here — using a
  // fixed total height would clip a card whose content is taller than
  // `cardHeight`, running it into the next one.
  const representativesHeight = () => {
    if (representatives.length === 0) return 0;
    if (expandedIndex !== null) {
      return (
        (representatives.length - 1) * representativeHeight + expandedHeight
      );
    }
    return representatives.length * representativeHeight;
  };

  const tailHeight = representativesHeight();

  const handleRepClick = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleMainCardClick = () => {
    if (expandedIndex !== null) {
      setExpandedIndex(null);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: cardWidth,
        // Height comes from the main card plus room for the representative
        // strip; it is not pinned, so the card can grow with its content.
        paddingBottom: `${tailHeight}px`,
        verticalAlign: 'top',
      }}
    >
      {/* کارت‌های نماینده - رندر شدن بر اساس تعداد نماینده‌ها */}
      {representatives.length > 0 &&
        representatives.map((rep, index) => {
          const isExpanded = expandedIndex === index;
          return (
            <Paper
              key={index}
              elevation={isExpanded ? 3 : 1}
              onClick={() => handleRepClick(index)}
              sx={{
                position: 'absolute',
                bottom: isExpanded
                  ? (representatives.length - 1) * representativeHeight
                  : index * representativeHeight,
                left: 0,
                width: cardWidth,
                height: isExpanded ? expandedHeight : representativeHeight,
                background: isExpanded
                  ? 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 50%, #A5B4FC 100%)'
                  : 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 50%, #C7D2FE 100%)',
                border: '1px solid #C7D2FE',
                borderRadius: index === 0 ? '0 0 8px 8px' : '0',
                zIndex: isExpanded ? representatives.length + 2 : index,
                display: 'flex',
                flexDirection: isExpanded ? 'column' : 'row',
                alignItems: isExpanded ? 'stretch' : 'center',
                justifyContent: isExpanded ? 'flex-start' : 'space-between',
                direction: 'rtl',
                padding: isExpanded ? '12px 16px' : '0 16px',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: 'slideInFromBottom 0.5s ease-out',
                animationDelay: `${index * 0.1}s`,
                animationFillMode: 'backwards',
                transform: isExpanded ? 'scale(1.02)' : 'scale(1)',
                '&:hover': {
                  background: isExpanded
                    ? 'linear-gradient(135deg, #C7D2FE 0%, #A5B4FC 50%, #818CF8 100%)'
                    : 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 50%, #A5B4FC 100%)',
                  transform: isExpanded ? 'scale(1.02)' : 'translateX(-2px)',
                  boxShadow: isExpanded
                    ? '0 8px 16px rgba(165, 180, 252, 0.4)'
                    : '0 4px 8px rgba(165, 180, 252, 0.3)',
                },
                '&:active': {
                  transform: 'scale(0.98)',
                  transition: 'transform 0.1s ease',
                },
                '@keyframes slideInFromBottom': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(20px)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {/* حالت collapse - فقط نمایش اسم */}
              {!isExpanded && (
                <>
                  <Typography
                    sx={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#3730A3',
                    }}
                  >
                    نماینده:
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'IRANYekanXFaNum',
                      fontSize: '11px',
                      fontWeight: 500,
                      color: '#4338CA',
                    }}
                  >
                    {rep.name}
                  </Typography>
                </>
              )}

              {/* حالت expanded - نمایش اطلاعات کامل و دکمه‌ها */}
              {isExpanded && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'IRANYekanXFaNum',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#3730A3',
                      }}
                    >
                      {rep.name}
                    </Typography>
                  </Box>

                  {/* دکمه‌های عملیات */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {/* دکمه حذف نماینده */}
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveRepresentative?.(rep);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        animation: 'fadeInRight 0.4s ease-out',
                        animationDelay: '0.1s',
                        animationFillMode: 'backwards',
                        '&:hover': {
                          background: 'rgba(55, 48, 163, 0.15)',
                          transform: 'translateX(-2px)',
                        },
                        '&:active': {
                          transform: 'scale(0.95)',
                        },
                        '@keyframes fadeInRight': {
                          '0%': {
                            opacity: 0,
                            transform: 'translateX(10px)',
                          },
                          '100%': {
                            opacity: 1,
                            transform: 'translateX(0)',
                          },
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          color: '#3730A3',
                          padding: '4px',
                        }}
                      >
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{
                          fontFamily: 'IRANYekanXFaNum',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: '#3730A3',
                        }}
                      >
                        حذف نماینده
                      </Typography>
                    </Box>

                    {/* دکمه حذف دسترسی */}
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        onRevokeAccess?.(rep);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        animation: 'fadeInRight 0.4s ease-out',
                        animationDelay: '0.2s',
                        animationFillMode: 'backwards',
                        '&:hover': {
                          background: 'rgba(55, 48, 163, 0.15)',
                          transform: 'translateX(-2px)',
                        },
                        '&:active': {
                          transform: 'scale(0.95)',
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        sx={{
                          color: '#3730A3',
                          padding: '4px',
                        }}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{
                          fontFamily: 'IRANYekanXFaNum',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: '#3730A3',
                        }}
                      >
                        حذف دسترسی
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          );
        })}

      {/* کارت اصلی
          In flow (not absolute) so its real height drives the wrapper; the
          representative strip is reserved via the wrapper's padding-bottom. */}
      <Box
        onClick={handleMainCardClick}
        sx={{
          position: 'relative',
          width: '100%',
          zIndex: representatives.length + 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default RepresentativeCard;
